import { useLocation, useNavigate } from 'react-router-dom';
import { linkClassName } from '@monecolier/ui';
import type { AccessRedirectState } from './RequireRouteAccess';

function isAccessRedirectState(state: unknown): state is AccessRedirectState {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as { accessRedirect?: unknown }).accessRedirect === true
  );
}

/**
 * UX-79 — bandeau discret affiché sur l'accueil après une redirection de
 * `RequireRouteAccess`. Ton informatif, jamais alarmant (même discipline que
 * PAY-01/UX-40) : l'utilisateur n'a rien fait de mal. `role="status"` le fait
 * annoncer par les lecteurs d'écran sans voler le focus.
 *
 * Porté par l'état de l'entrée d'historique : il disparaît dès qu'on navigue
 * ailleurs. "Fermer" remplace l'entrée par la même URL SANS cet état, pour
 * qu'un rechargement ou un retour arrière ne le fasse pas réapparaître.
 */
export function AccessRedirectNotice() {
  const location = useLocation();
  const navigate = useNavigate();

  if (!isAccessRedirectState(location.state)) return null;

  return (
    <div
      role="status"
      data-testid="access-redirect-notice"
      className="mb-4 flex w-full items-center justify-between gap-3 rounded-md border border-brand-300 bg-brand-100 px-4 py-2 text-sm text-brand-800"
    >
      <p>Vous n&rsquo;avez pas accès à cette page. Voici votre page d&rsquo;accueil.</p>
      <button
        type="button"
        onClick={() =>
          navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
        }
        className={`${linkClassName} shrink-0`}
        aria-label="Fermer ce message"
        data-testid="access-redirect-notice-close"
      >
        Fermer
      </button>
    </div>
  );
}
